import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  CircularProgress,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Save } from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { supabase } from "../../utils/API";
import { Notify } from "../../layouts/notify";
import { DefaultPage } from "../../layouts/default-page";

const PAGE_SLUG = "who-is-board-deputy";
const DEFAULT_TITLE = "Who is a Board Deputy?";

export default function AppPageForm() {
  const [content, setContent] = useState("");
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageId, setPageId] = useState<string | null>(null);

  useEffect(() => {
    fetchPage();
  }, []);

  const fetchPage = async () => {
    try {
      const { data, error } = await supabase
        .from("app_pages")
        .select("*")
        .eq("slug", PAGE_SLUG)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setContent(data.content || "");
        setTitle(data.title || DEFAULT_TITLE);
        setPageId(data.id);
      }
    } catch (err: any) {
      Notify("error", "Failed to load page content.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (pageId) {
        // Update existing
        const { error } = await supabase
          .from("app_pages")
          .update({ title, content, updated_at: new Date() })
          .eq("id", pageId);
        if (error) throw error;
      } else {
        // Create new
        const { data, error } = await supabase
          .from("app_pages")
          .insert([{ slug: PAGE_SLUG, title, content }])
          .select()
          .single();
        if (error) throw error;
        setPageId(data.id);
      }
      Notify("success", "Page content saved successfully!");
    } catch (err: any) {
      Notify("error", "Failed to save page.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DefaultPage
      title="Who is a Board Deputy"
      subTitle="Manage informational page content for the mobile app."
    >
      <Paper
        sx={{
          p: 3.5,
          maxWidth: 900,
          borderRadius: "6px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography fontWeight={700} fontSize={16} color="#0F172A" mb={0.5}>
              Page Information
            </Typography>
            <Typography fontSize={13} color="#64748B">
              Edit the title and formatted rich-text description displayed in the mobile application.
            </Typography>
          </Box>

          <TextField
            label="Page Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "4px",
                fontSize: "14px",
              },
            }}
          />

          <Box sx={{ height: 400, mb: 5 }}>
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={modules}
              style={{ height: "100%" }}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
            <LoadingButton
              variant="contained"
              loading={saving}
              onClick={handleSave}
              startIcon={<Save />}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                px: 3,
                backgroundColor: "#0072BC",
                "&:hover": { backgroundColor: "#005a96" },
              }}
            >
              Save Changes
            </LoadingButton>
          </Box>
        </Stack>
      </Paper>
    </DefaultPage>
  );
}
